import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { orderBy, sortBy, sumBy } from 'lodash';
import moment from 'moment';
import React from 'react';
import { TreeNodeData } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Tree } from 'src/design/molecules/Tree';
import { IntegrationStatusDto } from 'src/api/FiretrolApiClient';
import { useCtcBreakdown } from 'src/api/queries/ctc/useCtcBreakdown';
import { useCtcMaterialsForCode } from 'src/api/queries/ctc/useCtcMaterials';
import { formatCurrency } from 'src/design/atoms/Currency';
import { useAuth } from 'src/hooks/useAuth';
import { POTab } from './POTab';

function cleanNumber(value: any) {
  if (typeof value === 'string' && value.includes('(')) {
    if (value.length === 0) {
      return 0;
    }
    const tmp = value.replace('(', '').replace(')', '');
    return Number(tmp) * -1;
  }
  return Number(value);
}

interface BreakdownDialogProps {
  handleClose: () => void;
  jobNumber: string;
  costCode: string | null;
  isLabor: boolean;
  description: string | null;
  isHours?: boolean;
  isMtd?: boolean;
  totalValue?: number;
  status: IntegrationStatusDto | null;
}

export const BreakdownDialog: React.FC<BreakdownDialogProps> = ({
  handleClose,
  costCode,
  isLabor,
  jobNumber,
  totalValue,
  isHours = false,
  description,
  isMtd,
  status,
}) => {
  const { checkFeatureAllowed } = useAuth();
  const [tab, setTab] = React.useState<'po' | 'nonpo' | 'all'>('all');
  React.useEffect(() => {
    if (costCode) setTab('all');
  }, [costCode]);
  const hasLaborDetailAccess = checkFeatureAllowed(['ctc_labor_rates']);

  const breakdownQuery = useCtcBreakdown(jobNumber, costCode);
  const materialQuery = useCtcMaterialsForCode(jobNumber, costCode, !isLabor);
  const loading = breakdownQuery.isLoading || materialQuery.isLoading;

  // The MTD view shows only transactions posted in the accounting period the
  // integration is currently calculating, which trails the calendar month.
  const currMonth = React.useMemo(
    () =>
      status?.mtdCalcMonth ? moment().month(status.mtdCalcMonth - 1) : moment(),
    [status?.mtdCalcMonth],
  );

  const breakdown = React.useMemo(() => {
    const data = breakdownQuery.data ?? {};
    if (!isMtd) return data;
    return Object.keys(data).reduce<typeof data>((acc, key) => {
      const filtered = data[key].filter((v) => {
        const docdate = v.docdate.toString();
        const tmp = new Date(docdate.slice(0, 10));
        return moment(
          new Date(tmp.getTime() - tmp.getTimezoneOffset() * -60000),
        ).isSame(currMonth, 'month');
      });
      if (filtered.length === 0) return acc;
      return { ...acc, [key]: filtered };
    }, {});
  }, [breakdownQuery.data, isMtd, currMonth]);

  const material = React.useMemo(() => {
    const data = materialQuery.data ?? {};
    if (!isMtd) return data;
    return Object.keys(data).reduce<typeof data>((acc, key) => {
      const filtered = data[key].filter((v) =>
        moment(String(v.docdate).slice(0, 10)).isSame(currMonth, 'month'),
      );
      if (filtered.length === 0) return acc;
      return { ...acc, [key]: filtered };
    }, {});
  }, [materialQuery.data, isMtd, currMonth]);

  const buildTreeData = (filterPO = false): TreeNodeData[] => {
    let grandTotal = 0;
    let grandTotalHours = 0;
    const nodes: TreeNodeData[] = [];

    sortBy(Object.keys(breakdown)).forEach((key) => {
      const values = breakdown[key];
      const numberValues = values.map((val) => ({
        ...val,
        hoursNumber: cleanNumber(val.hours),
        committedCostNumber: cleanNumber(val.amount),
      }));
      const tmpItems = orderBy(
        numberValues,
        (v) => new Date(String(v.docdate).slice(0, 10)),
        'desc',
      );
      const orderedValues = filterPO
        ? tmpItems.filter((v) => v.ponumber === '')
        : tmpItems;
      const total = sumBy(numberValues, 'committedCostNumber');
      const totalHours = sumBy(numberValues, 'hoursNumber');
      grandTotal = grandTotal + total;
      grandTotalHours = grandTotalHours + totalHours;

      const children: TreeNodeData[] = orderedValues.map((val) => ({
        value: val.docnumbr,
        label: `${val.docnumbr.length === 0 ? '' : val.docnumbr} (${String(
          val.docdate,
        )})`,
        nodeProps: {
          trxsorce: val.trxsorce,
          hours: isLabor ? String(val.hours) : undefined,
          amount: isLabor
            ? hasLaborDetailAccess
              ? formatCurrency(Number(val.amount))
              : '--'
            : formatCurrency(Number(val.amount)),
        },
      }));

      nodes.push({
        value: key,
        label: key.length === 0 ? 'No Description' : key.toLowerCase(),
        nodeProps: {
          hours: isLabor ? totalHours.toLocaleString() : undefined,
          amount: isLabor
            ? hasLaborDetailAccess
              ? formatCurrency(total)
              : '--'
            : formatCurrency(total),
        },
        children,
      });
    });

    nodes.push({
      value: 'grandTotal',
      label: 'Total',
      nodeProps: {
        isTotal: true,
        hours: isLabor ? grandTotalHours.toLocaleString() : undefined,
        amount: isLabor
          ? hasLaborDetailAccess
            ? formatCurrency(grandTotal)
            : '--'
          : formatCurrency(grandTotal),
      },
    });

    return nodes;
  };

  const nonPoTreeData = React.useMemo(() => buildTreeData(true), [breakdown]);
  const allTreeData = React.useMemo(() => buildTreeData(), [breakdown]);

  const renderTree = (data: TreeNodeData[]) => {
    if (Object.keys(breakdown).length === 0) {
      return (
        <div style={{ height: 300 }}>
          <p style={{ textAlign: 'center' }}>No Items</p>
        </div>
      );
    }
    return (
      <Box style={{ height: 400, overflow: 'auto' }}>
        <Grid mb="xs" px="xs">
          <Grid.Col span={3}>
            <Text fw={700} size="sm">
              Description
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text fw={700} size="sm" pl={10}>
              TRX
            </Text>
          </Grid.Col>
          <Grid.Col span={4} ta="right">
            {isLabor && (
              <Text fw={700} size="sm">
                Hours
              </Text>
            )}
          </Grid.Col>
          <Grid.Col span={2} ta="right">
            <Text fw={700} size="sm">
              Amount
            </Text>
          </Grid.Col>
        </Grid>
        <Tree
          data={data}
          expandOnClick
          renderNode={({ node, expanded, hasChildren, elementProps }) => {
            const props = node.nodeProps as any;
            const isLeaf = !hasChildren && !props?.isTotal;
            return (
              <Box {...elementProps}>
                <Grid px="xs">
                  <Grid.Col span={isLeaf ? 3 : 5}>
                    <Box
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      {hasChildren &&
                        (expanded ? (
                          <IconChevronDown size={16} />
                        ) : (
                          <IconChevronRight size={16} />
                        ))}
                      <Text
                        size="sm"
                        fw={hasChildren || props?.isTotal ? 700 : undefined}
                        style={{ textTransform: 'capitalize' }}
                      >
                        {node.label}
                      </Text>
                    </Box>
                  </Grid.Col>
                  {isLeaf && (
                    <Grid.Col span={3}>
                      <Text size="sm" style={{ textTransform: 'capitalize' }}>
                        {props?.trxsorce || ''}
                      </Text>
                    </Grid.Col>
                  )}
                  <Grid.Col span={isLeaf ? 4 : 3} ta="right">
                    {props?.hours && (
                      <Text
                        size="sm"
                        fw={hasChildren || props?.isTotal ? 700 : undefined}
                      >
                        {props.hours}
                      </Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={2} ta="right">
                    <Text
                      size="sm"
                      fw={hasChildren || props?.isTotal ? 700 : undefined}
                    >
                      {props?.amount || ''}
                    </Text>
                  </Grid.Col>
                </Grid>
              </Box>
            );
          }}
        />
      </Box>
    );
  };

  return (
    <BaseModal
      opened={costCode !== null}
      onClose={handleClose}
      title={`${description} - ${costCode}`}
      size="xl"
    >
      <Box p="md" pt={0}>
        {loading && (
          <p style={{ paddingLeft: 10, marginBottom: 100 }}>Loading...</p>
        )}
        {!loading && (
          <>
            {isLabor && <>{renderTree(nonPoTreeData)}</>}
            {!isLabor && (
              <div style={{ paddingLeft: 10 }}>
                <Group gap="xs" mb="lg">
                  <BasicButton
                    variant={tab === 'all' ? 'filled' : 'outline'}
                    onClick={() => setTab('all')}
                  >
                    INVOICED
                  </BasicButton>
                  <BasicButton
                    variant={tab === 'po' ? 'filled' : 'outline'}
                    onClick={() => setTab('po')}
                  >
                    PO
                  </BasicButton>
                </Group>
                {tab === 'all' && <>{renderTree(allTreeData)}</>}
                {tab === 'nonpo' && <>{renderTree(nonPoTreeData)}</>}
                {tab === 'po' && <POTab material={material} />}
              </div>
            )}
          </>
        )}
      </Box>
    </BaseModal>
  );
};
